import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../api";
import { StatCard, Card, Loading, Badge } from "../components/UI";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([API.get("/leads/stats"), API.get("/clients"), API.get("/leads")])
      .then(([s, c, l]) => { setStats(s.data); setClients(c.data); setRecentLeads(l.data.slice(0, 5)); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  return (
    <div className="fade-in">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 20 }}>
        <StatCard label="Total Clients"    value={clients.length}        icon="◉"  accent="77,159,255" />
        <StatCard label="Total Leads"      value={stats?.total || 0}     icon="◎"  accent="37,211,102" />
        <StatCard label="Bike Inquiries"   value={stats?.bikeLeads || 0} icon="🏍️" accent="255,149,0" />
        <StatCard label="Service Bookings" value={stats?.serviceLeads || 0} icon="🔧" accent="255,77,77" />
        <StatCard label="Hot Leads 🔥"    value={stats?.hot || 0}        icon="🔥" accent="255,77,77" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Recent Leads</span>
            <Link to="/leads" style={{ fontSize: 12, color: "var(--accent)", textDecoration: "none", fontFamily: "var(--font-mono)" }}>View all →</Link>
          </div>
          {recentLeads.length === 0 ? (
            <div style={{ color: "var(--text-muted)", fontSize: 13 }}>No leads yet. Use Bot Tester to generate leads.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>{["Phone", "Interest", "Score", "Time"].map(h => <th key={h} style={{ textAlign: "left", fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {recentLeads.map(lead => (
                  <tr key={lead._id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "10px 4px", fontSize: 12, fontFamily: "var(--font-mono)" }}>{lead.phone}</td>
                    <td style={{ padding: "10px 4px" }}><Badge color={lead.interest === "Bike Details" ? "orange" : "blue"}>{lead.interest}</Badge></td>
                    <td style={{ padding: "10px 4px" }}><Badge color={lead.score === "hot" ? "red" : lead.score === "warm" ? "orange" : "grey"}>{lead.score}</Badge></td>
                    <td style={{ padding: "10px 4px", fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{new Date(lead.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Active Clients</span>
            <Link to="/clients" style={{ fontSize: 12, color: "var(--accent)", textDecoration: "none", fontFamily: "var(--font-mono)" }}>View all →</Link>
          </div>
          {clients.length === 0 ? (
            <div style={{ color: "var(--text-muted)", fontSize: 13 }}>No clients. <Link to="/add-client" style={{ color: "var(--accent)" }}>Add one →</Link></div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {clients.slice(0, 5).map(c => (
                <div key={c._id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: "var(--bg-elevated)", border: "1px solid var(--border-light)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 13, color: "var(--accent)" }}>{c.brand?.charAt(0)}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{c.brand} · {c.city || "—"}</div>
                  </div>
                  <Badge color={c.isActive ? "green" : "grey"} style={{ marginLeft: "auto" }}>{c.isActive ? "Active" : "Inactive"}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card style={{ marginTop: 20 }}>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>Quick Actions</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {[
           { label: "Add New Client", to: "/app/add-client", icon: "⊕", color: "var(--accent)" },
{ label: "View All Leads", to: "/app/leads", icon: "◎", color: "var(--accent-blue)" },
{ label: "Test Bot", to: "/app/bot-tester", icon: "◆", color: "var(--accent-orange)" },
          ].map(a => (
            <Link key={a.to} to={a.to} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", border: "1px solid var(--border)", borderRadius: "var(--radius)", textDecoration: "none", background: "var(--bg-elevated)", color: a.color, fontSize: 13, fontWeight: 500 }}>
              <span style={{ fontSize: 18 }}>{a.icon}</span><span>{a.label}</span>
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
}

